// Serviço do acordo de parcelamento — criação, termo, efetivação, cancelamento.
// Chamado #5686.
//
// Divisão de responsabilidade, deliberada:
//
//   este arquivo (browser) ... monta e CRIA o acordo. Não toca em fatura-mãe,
//                              não cancela boleto, não carimba 'Parcelado'.
//   edge function .......... EFETIVA e CANCELA. É o único lugar do sistema que
//                            move as faturas originais.
//
// O motivo de a efetivação não morar aqui: ela também roda sozinha pelo cron,
// sem navegador. Duas implementações da mesma regra — uma no front para o botão
// "Efetivar agora", outra no servidor para o cron — divergiriam na primeira
// correção, e o que diverge aqui é cancelamento de cobrança de cliente.

import { supabase } from './supabase';
import { fetchSignatariosDefault } from './termoSignatarios';
import { normalizeCpfCnpj } from './utils';
import {
  anexoBPadrao, camposFaturaDoPagador, devedorPadrao, faturaParaTermo, montarPayloadTermo, pagadorDoAcordo, validarDadosTermo,
  type CadastroNegociacao, type CadastroUc, type DadosTermo,
} from './termoNegociacao.ts';
import { normalizeMesReferencia } from './mesReferencia';
import { emitirEEnviarEntrada } from './entradaAcordo';
import type { EntradaEnvio } from './entradaAcordoRegras.ts';
import type { ResultadoAcordo, SaldoNegociado } from './parcelamentoCalculo.ts';
import { paraReais } from './parcelamentoCalculo.ts';
// `export ... from` reexporta mas NÃO traz o nome para o escopo deste arquivo —
// por isso o import explícito dos que são usados aqui dentro.
import {
    analisarGateway, avaliarAlcada, ESTADOS_PENDENTES, fraseDesconto, resolverContaCobranca,
    type LimitesAlcada, type StatusAcordo,
} from './parcelamentoRegras.ts';
// A mesma regra que a varredura usa para não expirar proposta com entrada
// compensando — um lugar só, para as duas pontas não divergirem.
import { limiteCompensacaoEntrada } from '../../supabase/functions/_shared/efetivacaoParcelamento.ts';
// A tela mostra o botão de cancelar pela mesma regra que a edge function aplica.
export { podeCancelarAcordo } from '../../supabase/functions/_shared/efetivacaoParcelamento.ts';
import { mesDaFaturaDaParcela } from '../../supabase/functions/_shared/parcelaNaFaturaVigor.ts';

// Regras puras moram em parcelamentoRegras.ts (sem I/O, cobertas por teste).
// Reexportadas aqui para as telas importarem de um lugar só.
export {
    acordoPendente, acordoVigente, analisarGateway, avaliarAlcada, ESTADOS_PENDENTES,
    ESTADOS_VIGENTES, mesmoPagador, resolverContaCobranca, ROTULO_STATUS,
} from './parcelamentoRegras.ts';
export type {
    AvisoGateway, ContaCobranca, LimitesAlcada, MotivoAprovacao, Pagador, StatusAcordo,
} from './parcelamentoRegras.ts';

export const PARCELAMENTO_TERMO_WEBHOOK_KEY = 'parcelamento_termo_webhook_url';
export const PARCELAMENTO_PRAZO_VALIDADE_KEY = 'parcelamento_prazo_validade_dias';
export const PARCELAMENTO_TAXA_MINIMA_KEY = 'parcelamento_taxa_juros_minima';
export const PARCELAMENTO_DESCONTO_MAXIMO_KEY = 'parcelamento_desconto_saldo_maximo';

export interface FaturaDoAcordo {
  id: string | number;
  uc_codigo?: string | null;
  cliente_nome?: string | null;
  mes_referencia?: string | null;
  valor_boleto_vigor?: number | null;
  vencimento_boleto?: string | null;
  status_boleto_vigor?: string | null;
  status_fatura?: string | null;
  num_cobranca_boleto?: string | null;
  conta_sicoob?: string | null;
  empresa_parceira_id?: string | null;
}

export async function buscarLimitesAlcada(): Promise<LimitesAlcada> {
  const { data } = await supabase
    .from('system_settings')
    .select('key, value')
    .in('key', [PARCELAMENTO_TAXA_MINIMA_KEY, PARCELAMENTO_DESCONTO_MAXIMO_KEY]);
  const map = new Map((data || []).map((r: any) => [r.key, r.value]));
  return {
    taxaJurosMinima: Number(map.get(PARCELAMENTO_TAXA_MINIMA_KEY) ?? 0) || 0,
    descontoSaldoMaximo: Number(map.get(PARCELAMENTO_DESCONTO_MAXIMO_KEY) ?? 100) || 100,
  };
}

export async function buscarPrazoValidadeDias(): Promise<number> {
  const { data } = await supabase
    .from('system_settings')
    .select('value')
    .eq('key', PARCELAMENTO_PRAZO_VALIDADE_KEY)
    .maybeSingle();
  const n = Number(data?.value);
  return Number.isFinite(n) && n > 0 ? n : 7;
}

/**
 * Validade da proposta: o prazo configurado, mas nunca antes de a entrada ter
 * vencido e tido tempo de compensar. A varredura aplica a mesma folga; gravar a
 * data já estendida faz a tela mostrar o prazo real.
 */
async function calcularExpiracao(vencimentoEntrada: string | null): Promise<string> {
  const prazoDias = await buscarPrazoValidadeDias();
  const porPrazo = new Date(Date.now() + prazoDias * 86400000);
  if (!vencimentoEntrada) return porPrazo.toISOString();
  const porEntrada = new Date(`${limiteCompensacaoEntrada(vencimentoEntrada)}T23:59:59-03:00`);
  return (porEntrada > porPrazo ? porEntrada : porPrazo).toISOString();
}

// ---------------------------------------------------------------------------
// Trilha de eventos
// ---------------------------------------------------------------------------

export type TipoEvento =
  | 'criado' | 'termo_enviado' | 'termo_assinado' | 'entrada_paga'
  | 'efetivado' | 'aprovado' | 'cancelado' | 'expirado' | 'falha'
  | 'em_atraso' | 'regularizado' | 'alerta' | 'entrada_enviada' | 'termo_visualizado' | 'link_enviado';

/** Best-effort: a trilha nunca pode derrubar a operação que ela registra. Mas
 *  a falha vai para o console — trilha silenciosamente vazia é pior que erro. */
export async function registrarEvento(
  parcelamentoId: string,
  tipo: TipoEvento,
  descricao: string,
  dados?: Record<string, unknown>,
  usuario?: { id?: string | null; nome?: string | null },
): Promise<void> {
  try {
    const { error } = await supabase.from('parcelamento_eventos').insert({
      parcelamento_id: parcelamentoId,
      tipo,
      descricao,
      dados: dados ?? null,
      usuario_id: usuario?.id ?? null,
      usuario_nome: usuario?.nome ?? null,
    });
    if (error) throw error;
  } catch (err) {
    console.error('[parcelamento] falha ao gravar evento da trilha', tipo, err);
  }
}

// ---------------------------------------------------------------------------
// Criação
// ---------------------------------------------------------------------------

export interface DadosCriacaoAcordo {
  faturas: FaturaDoAcordo[];
  unidadeConsumidoraId: number | null;
  ucCodigo: string;
  clienteNome: string | null;
  tipoContrato: 'divida' | 'divida_saldo';
  saldo: SaldoNegociado | null;
  multaPercent: number;
  moraMensalPercent: number;
  resultado: ResultadoAcordo;
  descricao: string;
  usuario: { id?: string | null; nome?: string | null };
  /** Partes e anexos do termo, como o analista conferiu na tela. */
  termoDados: DadosTermo | null;
  /** Parcelas somadas no boleto mensal da Vigor (17/10/2026). Ausente = cada
   *  parcela com boleto próprio. `defasagemMeses` vem de `defasagemDaUc`. */
  parcelasNaFaturaVigor?: { defasagemMeses: number } | null;
}

export interface AcordoCriado {
  id: string;
  status: StatusAcordo;
  entradaFaturaId: number | null;
  avisos: string[];
}

/**
 * Cria o acordo em estado PENDENTE e emite apenas a cobrança da entrada.
 *
 * O que este método deliberadamente NÃO faz: mexer nas faturas selecionadas.
 * Elas seguem vencidas, com boleto vivo, até a efetivação. É a regra do chamado
 * ("o parcelamento só vai valer a partir do momento que for assinado e pago a
 * entrada") e é o que impede que um acordo abandonado destrua a dívida original.
 */
export async function criarAcordo(dados: DadosCriacaoAcordo): Promise<AcordoCriado> {
  const { resultado, faturas, usuario } = dados;
  const avisos: string[] = [];
  // De onde veio o desconto (#6174). Vem nos dados do termo porque muda o texto
  // dele; aqui vira coluna e evento, para o histórico separar uma coisa da outra.
  const origemDesconto = resultado.descontoDividaCentavos > 0 ? dados.termoDados?.desconto ?? null : null;

  // Juntar faturas de contas, empresas ou UCs diferentes NÃO barra mais o
  // acordo: a dívida é do titular, e o acordo é uma cobrança nova. O que cada
  // divergência produz é um aviso — e uma conta de cobrança escolhida sempre
  // pela mesma regra (`resolverContaCobranca`).
  const avisosGateway = analisarGateway(faturas);
  avisosGateway.forEach((a) => avisos.push(a.mensagem));

  const limites = await buscarLimitesAlcada();
  const motivosAprovacao = avaliarAlcada(
    resultado.taxaJurosMensalPercent,
    dados.saldo ? dados.saldo.descontoPercent : null,
    limites,
  );
  const precisaAprovacao = motivosAprovacao.length > 0;

  // A mesma fatura em duas propostas vivas seria carimbada duas vezes quando as
  // duas efetivassem — o cliente pagaria em dobro. A busca do modal só esconde
  // fatura JÁ carimbada; proposta pendente ainda não carimba nada.
  if (faturas.length > 0) {
    const { data: pendentes, error: erroPendentes } = await supabase
      .from('parcelamentos')
      .select('id, cliente_nome_snapshot, faturas_origem_snapshot')
      .in('status', [...ESTADOS_PENDENTES]);
    if (erroPendentes) throw erroPendentes;
    const selecionadas = new Set(faturas.map((f) => String(f.id)));
    for (const p of pendentes || []) {
      const repetidas = ((p.faturas_origem_snapshot as any[]) || [])
        .filter((s) => selecionadas.has(String(s.id)))
        .map((s) => s.mes_referencia || `#${s.id}`);
      if (repetidas.length > 0) {
        throw new Error(
          `A(s) fatura(s) ${repetidas.join(', ')} já estão numa proposta de acordo ainda não assinada` +
          `${p.cliente_nome_snapshot ? ` (${p.cliente_nome_snapshot})` : ''}. Cancele aquela proposta antes de criar outra.`,
        );
      }
    }
  }

  const expiraEm = await calcularExpiracao(resultado.vencimentoEntrada);

  // Snapshot do estado ANTERIOR das faturas. Sem isso, desfazer um acordo é
  // adivinhar se cada fatura era 'Vencido' ou 'Em aberto'.
  const snapshot = faturas.map((f) => ({
    id: f.id,
    status_boleto_vigor: f.status_boleto_vigor ?? null,
    status_fatura: f.status_fatura ?? null,
    num_cobranca_boleto: f.num_cobranca_boleto ?? null,
    valor_boleto_vigor: f.valor_boleto_vigor ?? null,
    mes_referencia: f.mes_referencia ?? null,
  }));

  const { data: acordo, error: erroAcordo } = await supabase
    .from('parcelamentos')
    .insert({
      unidade_consumidora_id: dados.unidadeConsumidoraId,
      uc_codigo_snapshot: dados.ucCodigo || null,
      cliente_nome_snapshot: dados.clienteNome,
      descricao: dados.descricao.trim() || null,
      tipo_contrato: dados.tipoContrato,

      // `valor_total` é a coluna histórica e continua sendo o TOTAL do acordo
      // (entrada + parcelas). As telas antigas leem ela.
      valor_total: paraReais(resultado.totalAcordoCentavos),
      numero_parcelas: resultado.numeroParcelas,

      divida_bruta: paraReais(resultado.dividaBrutaCentavos),
      valor_faturas: paraReais(resultado.valorFaturasCentavos),
      multa_percent: dados.multaPercent,
      mora_mensal_percent: dados.moraMensalPercent,
      multa_total: paraReais(resultado.multaTotalCentavos),
      mora_total: paraReais(resultado.moraTotalCentavos),

      saldo_kwh: dados.saldo?.kwh ?? null,
      saldo_tarifa: dados.saldo?.tarifa ?? null,
      saldo_desconto_percent: dados.saldo?.descontoPercent ?? null,
      saldo_valor: dados.saldo ? paraReais(resultado.valorSaldoLiquidoCentavos) : null,

      // Só vai quando há desconto: a coluna entrou depois (migration
      // 20260930170005). Se o front subir antes dela, o acordo SEM desconto —
      // quase todos — continua sendo criado; só o com desconto falha, e diz o motivo.
      // Origem e descrição idem (migration 20261007120000, chamado #6174).
      ...(resultado.descontoDividaCentavos > 0
        ? {
          desconto_divida: paraReais(resultado.descontoDividaCentavos),
          desconto_origem: origemDesconto?.origem ?? null,
          desconto_descricao: origemDesconto?.descricao.trim() || null,
        }
        : {}),
      // Outros débitos (08/10/2026): mesma regra — só vai quando há, para o
      // acordo comum não depender da migration 20261009131816. A descrição de
      // cada um fica na memória de cálculo, que é o que o termo lê.
      ...(resultado.outrosDebitosCentavos > 0
        ? { outros_debitos: paraReais(resultado.outrosDebitosCentavos) }
        : {}),

      entrada_valor: paraReais(resultado.entradaCentavos),
      entrada_percentual: Number(resultado.entradaPercentual.toFixed(4)),
      entrada_vencimento: resultado.vencimentoEntrada,

      taxa_juros_mensal: resultado.taxaJurosMensalPercent,
      valor_financiado: paraReais(resultado.valorFinanciadoCentavos),
      juros_total: paraReais(resultado.jurosTotalCentavos),
      total_acordo: paraReais(resultado.totalAcordoCentavos),

      // Mesma regra: só vai quando o analista escolheu somar (migration
      // 20261017144042). O acordo comum não depende dela.
      ...(dados.parcelasNaFaturaVigor ? { cobranca_parcelas: 'fatura_vigor' } : {}),

      memoria_calculo: resultado as unknown as Record<string, unknown>,
      faturas_origem_snapshot: snapshot,
      termo_dados: dados.termoDados,

      status: precisaAprovacao ? 'aguardando_aprovacao' : 'aguardando_assinatura',
      aprovacao_necessaria: precisaAprovacao,
      expira_em: expiraEm,
      criado_por: usuario.id || null,
    })
    .select('id, status')
    .single();
  if (erroAcordo) throw erroAcordo;

  const acordoId = acordo.id as string;

  // Entrada é a parcela 0 (espelha a linha "00" da tela da Equatorial).
  const linhas = [
    {
      parcelamento_id: acordoId,
      numero_parcela: 0,
      tipo: 'entrada',
      valor: paraReais(resultado.entradaCentavos),
      mes_referencia: normalizeMesReferencia(mesDe(resultado.vencimentoEntrada)),
      data_vencimento: resultado.vencimentoEntrada,
      status: 'pendente',
      juros: 0,
    },
    ...resultado.parcelas.map((p) => ({
      parcelamento_id: acordoId,
      numero_parcela: p.numeroParcela,
      tipo: 'parcela',
      valor: paraReais(p.valorCentavos),
      // Somada no boleto da Vigor, a parcela guarda o mês da FATURA em que entra
      // (o boleto da fatura de OUT vence em NOV): é por ele que o cálculo da
      // fatura mensal a acha.
      mes_referencia: dados.parcelasNaFaturaVigor
        ? mesDaFaturaDaParcela(p.dataVencimento, dados.parcelasNaFaturaVigor.defasagemMeses)
        : normalizeMesReferencia(p.mesReferencia),
      data_vencimento: p.dataVencimento,
      status: 'pendente',
      juros: paraReais(p.jurosCentavos),
      amortizacao: paraReais(p.amortizacaoCentavos),
      saldo_inicial: paraReais(p.saldoInicialCentavos),
      saldo_final: paraReais(p.saldoFinalCentavos),
    })),
  ];

  const { error: erroParcelas } = await supabase.from('parcelamento_parcelas').insert(linhas);
  if (erroParcelas) throw erroParcelas;

  // Cobrança da entrada. Só ela — as parcelas viram fatura na efetivação, para
  // não existir boleto de parcela de um acordo que talvez nunca valha.
  let entradaFaturaId: number | null = null;
  try {
    entradaFaturaId = await criarFaturaEntrada(acordoId, dados, resultado);
    await supabase.from('parcelamentos').update({ entrada_fatura_id: entradaFaturaId }).eq('id', acordoId);
    await supabase
      .from('parcelamento_parcelas')
      .update({ status: 'provisionado', fatura_id: entradaFaturaId })
      .eq('parcelamento_id', acordoId)
      .eq('tipo', 'entrada');
  } catch (err: any) {
    // Acordo sem cobrança de entrada é inútil, mas apagar o que já foi criado é
    // pior: o operador perde o trabalho e não sabe o que houve. Fica o aviso.
    avisos.push(
      `O acordo foi criado, mas a cobrança da entrada não pôde ser gerada (${err?.message || err}). ` +
      'Gere a cobrança da entrada manualmente antes de enviar o termo.',
    );
  }

  const brl = (centavos: number) => paraReais(centavos).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const frase = fraseDesconto(origemDesconto?.origem, brl(resultado.descontoDividaCentavos));
  await registrarEvento(
    acordoId,
    'criado',
    `Acordo criado em ${resultado.numeroParcelas}x com entrada de ${brl(resultado.entradaCentavos)} e juros de ${resultado.taxaJurosMensalPercent}% a.m.`
      + (resultado.outrosDebitosCentavos > 0
        ? ` Soma ${brl(resultado.outrosDebitosCentavos)} de outros débitos: ${resultado.detalheOutrosDebitos.map((d) => `${d.descricao} (${brl(d.valorCentavos)})`).join('; ')}.`
        : '')
      + (resultado.descontoDividaCentavos > 0
        ? ` Com ${frase}${origemDesconto?.descricao.trim() ? ` (${origemDesconto.descricao.trim()})` : ''}.`
        : '')
      + (dados.parcelasNaFaturaVigor
        ? ' As parcelas vão somadas no boleto mensal da Vigor.'
        : ''),
    {
      faturas: faturas.map((f) => f.id),
      tipo_contrato: dados.tipoContrato,
      cobranca_parcelas: dados.parcelasNaFaturaVigor ? 'fatura_vigor' : 'avulsa',
      divida_bruta: paraReais(resultado.dividaBrutaCentavos),
      outros_debitos: resultado.detalheOutrosDebitos.map((d) => ({ descricao: d.descricao, valor: paraReais(d.valorCentavos) })),
      desconto_divida: paraReais(resultado.descontoDividaCentavos),
      desconto_origem: origemDesconto?.origem ?? null,
      desconto_descricao: origemDesconto?.descricao.trim() || null,
      total_acordo: paraReais(resultado.totalAcordoCentavos),
      aprovacao_necessaria: precisaAprovacao,
      motivos_aprovacao: motivosAprovacao.map((m) => m.motivo),
      expira_em: expiraEm,
      // Acordo que mistura conta, empresa ou UC é permitido, mas fica
      // rastreável: sem isto, descobrir depois por que o dinheiro caiu nesta
      // conta exigiria reconstruir a seleção de faturas na mão.
      conta_cobranca: resolverContaCobranca(faturas),
      avisos_gateway: avisosGateway.map((a) => a.campo),
    },
    usuario,
  );

  if (precisaAprovacao) {
    avisos.push(
      `O acordo precisa de aprovação antes de seguir: ${motivosAprovacao.map((m) => m.motivo).join(' ')}`,
    );
  }

  return { id: acordoId, status: acordo.status as StatusAcordo, entradaFaturaId, avisos };
}

function mesDe(dataIso: string): string {
  const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
  const [ano, mes] = dataIso.slice(0, 10).split('-').map(Number);
  return `${MESES[mes - 1]}/${ano}`;
}

/** Cria a fatura avulsa que carrega o boleto da entrada. Os campos seguem o
 *  mesmo contrato das faturas de parcela já usadas pelo módulo (ver comentários
 *  em CriarParcelamentoModal: valor_parcelamentos_boleto, status
 *  'Com boleto calculado', classificacao AVULSO). */
async function criarFaturaEntrada(
  acordoId: string,
  dados: DadosCriacaoAcordo,
  resultado: ResultadoAcordo,
): Promise<number> {
  const ref = await buscarReferenciaCobranca(dados);
  // Responsável que assumiu a dívida, com a opção "boletos no nome dele": a
  // entrada já nasce no nome dele. As parcelas copiam o pagador desta fatura na
  // efetivação (efetivar-parcelamentos), então basta acertar aqui.
  const pagadorAcordo = pagadorDoAcordo(dados.termoDados);

  const { data, error } = await supabase
    .from('faturas')
    .insert({
      uc_codigo: dados.ucCodigo,
      cliente_nome: dados.clienteNome,
      cliente_cpf_cnpj: ref.documento || null,
      nome_cliente_boleto: ref.nomePagador,
      cpf_cnpj_cliente_boleto: ref.documento || null,
      end_logradouro: ref.endereco?.end_logradouro ?? null,
      end_numero: ref.endereco?.end_numero ?? null,
      end_bairro: ref.endereco?.end_bairro ?? null,
      end_cep: ref.endereco?.end_cep ?? null,
      end_cidade: ref.endereco?.end_cidade ?? null,
      end_uf: ref.endereco?.end_uf ?? null,
      ...(pagadorAcordo ? camposFaturaDoPagador(pagadorAcordo) : {}),
      mes_referencia: normalizeMesReferencia(mesDe(resultado.vencimentoEntrada)),
      valor_parcelamentos_boleto: paraReais(resultado.entradaCentavos),
      valor_boleto_vigor: paraReais(resultado.entradaCentavos),
      total_com_vigor: 0,
      data_vencimento: resultado.vencimentoEntrada,
      vencimento_boleto: resultado.vencimentoEntrada,
      parcelamento_descricao: `${dados.descricao.trim() || 'Acordo de parcelamento'} - ENTRADA`,
      parcelamento_origem_id: acordoId,
      conta_sicoob: ref.conta_sicoob ?? null,
      empresa_parceira_id: ref.empresa_parceira_id ?? null,
      ambiente: ref.ambiente ?? 'producao',
      status_fatura: 'Com boleto calculado',
      status_boleto_vigor: 'Em aberto',
      classificacao: 'AVULSO',
    })
    .select('id')
    .single();
  if (error) throw error;
  return data.id as number;
}

async function buscarReferenciaCobranca(dados: DadosCriacaoAcordo) {
  let ref: any = null;
  let endereco: any = null;
  if (dados.faturas.length > 0) {
    const { data } = await supabase
      .from('faturas')
      .select(
        'id, cliente_cpf_cnpj, nome_cliente_boleto, cpf_cnpj_cliente_boleto, conta_sicoob, empresa_parceira_id, ambiente, end_logradouro, end_numero, end_bairro, end_cep, end_cidade, end_uf',
      )
      .in('id', dados.faturas.map((f) => f.id) as any[]);
    const linhas = data || [];
    // Conta, empresa e pagador saem da fatura MAIS RECENTE, não da primeira da
    // lista: é ela que reflete como o cliente está sendo cobrado hoje. Com
    // faturas de contas ou UCs diferentes, esta é a única escolha do acordo —
    // e é a mesma regra que a tela mostra ao operador antes de ele confirmar.
    const alvo = resolverContaCobranca(dados.faturas);
    ref = linhas.find((l: any) => String(l.id) === String(alvo.faturaId)) || linhas[0] || null;
    // Endereço vem de outra linha quando a de referência não tem CEP — fatura
    // antiga costuma ser a incompleta, e boleto sem endereço de pagador falha.
    endereco = ref?.end_cep ? ref : (linhas.find((l: any) => !!l.end_cep) || ref);
  }
  return {
    documento: normalizeCpfCnpj(ref?.cpf_cnpj_cliente_boleto || ref?.cliente_cpf_cnpj || ''),
    nomePagador: ref?.nome_cliente_boleto || dados.clienteNome || null,
    conta_sicoob: ref?.conta_sicoob ?? null,
    empresa_parceira_id: ref?.empresa_parceira_id ?? null,
    ambiente: ref?.ambiente ?? 'producao',
    endereco,
  };
}

// ---------------------------------------------------------------------------
// Termo, efetivação e cancelamento — tudo via edge function
// ---------------------------------------------------------------------------

/** Erro de `functions.invoke` engole a causa: em resposta não-2xx o SDK devolve
 *  só "Edge Function returned a non-2xx status code" e joga o motivo real em
 *  `error.context`. Mesmo padrão de src/lib/gatewayResolver.ts. */
async function invocar(fn: string, body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke(fn, { body });
  if (error) {
    const detalhe = await (error as any)?.context?.json?.().catch(() => null);
    if (detalhe?.error) throw new Error(detalhe.error);
    throw error;
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

export interface RespostaTermo {
  id_autentique: string;
  link_assinatura_cliente: string;
  /** Só no modelo único, quando há coobrigado. */
  link_assinatura_coobrigado?: string | null;
  numero_termo?: string;
  /** Canal em que o Autentique entregou o convite (#6202). Ausente no modelo antigo. */
  canal_devedor?: 'whatsapp' | 'email' | null;
  canal_coobrigado?: 'whatsapp' | 'email' | null;
  /** Resultado da emissão da entrada, feita junto com o termo (o envio espera a assinatura). */
  entrada?: EntradaEnvio;
}

/**
 * Dispara a geração do termo no n8n (Google Docs → Autentique).
 *
 * Valida `ok: true` explicitamente: o n8n devolve **200 com corpo vazio** quando
 * o fluxo morre antes do nó "Respond to Webhook". Tratar 200 como sucesso faria
 * o acordo ir para 'aguardando_assinatura' sem documento nenhum, e ninguém
 * descobriria até o cliente perguntar onde assina.
 */
/** " pelo WhatsApp" / " por e-mail (sem WhatsApp válido no cadastro)" — vazio no modelo antigo. */
export function descreverCanal(json: any): string {
  const c = json?.canal_devedor;
  if (c === 'whatsapp') return ' pelo WhatsApp';
  if (c === 'email') return ' por e-mail (sem WhatsApp válido no cadastro)';
  return '';
}

export async function solicitarTermo(
  acordoId: string,
  payload: Record<string, unknown>,
  usuario: { id?: string | null; nome?: string | null },
): Promise<RespostaTermo> {
  const { data: settings } = await supabase
    .from('system_settings')
    .select('value')
    .eq('key', PARCELAMENTO_TERMO_WEBHOOK_KEY)
    .maybeSingle();

  const url = settings?.value || '';
  if (!url) {
    throw new Error(
      `Webhook do termo de parcelamento não configurado em Config > APIs (chave ${PARCELAMENTO_TERMO_WEBHOOK_KEY}).`,
    );
  }

  let resposta: Response;
  try {
    resposta = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parcelamento_id: acordoId, ...payload }),
    });
  } catch {
    // O navegador só diz "Failed to fetch". Na prática é o fluxo desligado no
    // n8n (webhook inativo responde sem CORS) ou o n8n fora do ar.
    throw new Error(
      'não foi possível falar com o fluxo que gera o termo (n8n). Ele pode estar desligado — avise a Tecnologia. Nenhum documento foi criado.',
    );
  }

  const corpo = (await resposta.text()).trim();
  if (!resposta.ok) throw new Error(`O fluxo do termo respondeu HTTP ${resposta.status}. ${corpo.slice(0, 300)}`);
  if (!corpo) throw new Error('O fluxo do termo respondeu vazio — ele falhou antes de gerar o documento.');

  let json: any;
  try {
    json = JSON.parse(corpo);
  } catch {
    throw new Error(`O fluxo do termo respondeu algo que não é JSON: ${corpo.slice(0, 200)}`);
  }

  if (json?.ok !== true || !json?.id_autentique || !json?.link_assinatura_cliente) {
    throw new Error(
      `O fluxo do termo não confirmou a criação do documento. Resposta: ${corpo.slice(0, 300)}`,
    );
  }
  // Pediu coobrigado e o fluxo não devolveu o link dele: o documento existe,
  // mas a efetivação vai esperar uma assinatura que ninguém recebeu.
  if (payload.coobrigado && !json.link_assinatura_coobrigado) {
    throw new Error(
      'O fluxo criou o termo mas não devolveu o link do responsável pela dívida. Confira o documento no Autentique antes de reenviar.',
    );
  }

  const { error: erroGravar } = await supabase
    .from('parcelamentos')
    .update({
      termo_id_autentique: json.id_autentique,
      termo_url: json.link_assinatura_cliente,
      termo_url_coobrigado: json.link_assinatura_coobrigado || null,
      termo_numero: json.numero_termo || null,
      termo_enviado_em: new Date().toISOString(),
      termo_envio_erro: null,
      status: 'aguardando_assinatura',
    })
    .eq('id', acordoId);
  // O documento já foi criado no Autentique. Falhar calado aqui deixaria o
  // acordo sem o id do termo — a varredura nunca veria a assinatura.
  if (erroGravar) {
    throw new Error(
      `O termo ${json.numero_termo || ''} foi criado no Autentique (${json.id_autentique}), mas não consegui gravar no acordo: ${erroGravar.message}. Avise a Tecnologia antes de reenviar.`,
    );
  }

  await registrarEvento(
    acordoId,
    'termo_enviado',
    `Termo ${json.numero_termo || ''} enviado para assinatura ${payload.coobrigado ? 'do titular e do responsável pela dívida' : 'do cliente'}${descreverCanal(json)}${json.sandbox ? ' (TESTE — Autentique sandbox)' : ''}.`.replace('  ', ' '),
    {
      id_autentique: json.id_autentique,
      link: json.link_assinatura_cliente,
      link_coobrigado: json.link_assinatura_coobrigado || null,
      sandbox: !!json.sandbox,
      canal_devedor: json.canal_devedor ?? null,
      canal_coobrigado: json.canal_coobrigado ?? null,
    },
    usuario,
  );

  return json as RespostaTermo;
}

/**
 * Cadastro de hoje de quem deve: a UC e a negociação (assinante do termo de
 * adesão) — de onde saem nome, documento, endereço, e-mail e WhatsApp que o
 * termo pré-preenche. Pela UC escolhida ou pelo número (normal ou o outro
 * número da mesma UC).
 *
 * `ucCodigos` são tentados em ordem: o acordo sai na UC da fatura mais recente,
 * e quem deve em duas UCs pode ter o cadastro só na outra — sem tentar as
 * demais, a tela dizia "sem cadastro" e deixava e-mail e WhatsApp em branco.
 */
export async function buscarCadastroDevedor(ref: {
  ucId?: number | null;
  ucCodigo?: string | null;
  ucCodigos?: string[];
  /** Faturas do dono anterior da UC (troca de dono): o devedor é ele, não quem
   *  está hoje no cadastro. Nome, documento e contatos saem do pedido de troca;
   *  a negociação, da dele. */
  trocaDonoId?: string | null;
}): Promise<{
  ucId: number | null;
  uc: CadastroUc | null;
  negociacao: CadastroNegociacao | null;
}> {
  const COLS_UC = 'id, nome_cliente, cpf_cnpj_cliente, negociacao_id, email_cliente, email_cliente_2, whatsapp_cliente, whatsapp_cliente_2, endereco, logradouro, numero, bairro, cidade, cep';
  let uc: any = null;
  if (ref.ucId) {
    const { data } = await supabase.from('unidades_consumidoras').select(COLS_UC).eq('id', ref.ucId).maybeSingle();
    uc = data;
  } else {
    const normalizar = (v: string | null | undefined) => String(v || '').replace(/[.\-\s]/g, '').replace(/^0+/, '');
    const candidatos = [...new Set([ref.ucCodigo, ...(ref.ucCodigos || [])].map(normalizar).filter(Boolean))];
    for (const norm of candidatos) {
      const { data } = await supabase
        .from('unidades_consumidoras')
        .select(COLS_UC)
        .or(`numero_uc_norm.eq."${norm}",outro_numero_uc_norm.eq."${norm}"`)
        .order('id')
        .limit(1);
      uc = (data || [])[0] || null;
      if (uc) break;
    }
  }
  if (uc && ref.trocaDonoId) {
    const { data: troca } = await supabase
      .from('uc_trocas_dono')
      .select('negociacao_origem_id, dono_anterior')
      .eq('id', ref.trocaDonoId)
      .maybeSingle();
    if (troca) {
      const d: any = troca.dono_anterior || {};
      uc = {
        ...uc,
        nome_cliente: d.nome_cliente ?? null,
        cpf_cnpj_cliente: d.cpf_cnpj_cliente ?? null,
        email_cliente: d.email_cliente ?? null,
        email_cliente_2: d.email_cliente_2 ?? null,
        whatsapp_cliente: d.whatsapp_cliente ?? null,
        whatsapp_cliente_2: d.whatsapp_cliente_2 ?? null,
        negociacao_id: troca.negociacao_origem_id,
      };
    }
  }
  let negociacao: any = null;
  if (uc?.negociacao_id) {
    const { data } = await supabase
      .from('negociacoes')
      .select('cliente_nome, cpf_cnpj, email, whatsapp, nome_representante_legal, cpf_representante, data_assinatura_termo')
      .eq('id', uc.negociacao_id)
      .maybeSingle();
    negociacao = data;
  }
  return { ucId: uc?.id ?? null, uc, negociacao };
}

/** Termo de acordo criado ANTES do modelo único (sem `termo_dados`): monta as
 *  partes pelo cadastro de hoje e pelas faturas do snapshot. */
async function montarDadosTermoLegado(acordo: any): Promise<DadosTermo> {
  const ids = ((acordo.faturas_origem_snapshot as any[]) || []).map((f) => f.id);
  const { data: faturas } = ids.length
    ? await supabase
      .from('faturas')
      .select('id, uc_codigo, mes_referencia, num_cobranca_boleto, nome_cliente_boleto, cliente_nome, cpf_cnpj_cliente_boleto, cliente_cpf_cnpj, end_logradouro, end_numero, end_bairro, end_cidade, end_uf, end_cep')
      .in('id', ids)
    : { data: [] as any[] };
  const cadastro = await buscarCadastroDevedor({ ucId: acordo.unidade_consumidora_id, ucCodigo: acordo.uc_codigo_snapshot });
  const devedor = devedorPadrao({ negociacao: cadastro.negociacao, uc: cadastro.uc, fatura: (faturas || [])[0] || null });
  if (!devedor.nome) devedor.nome = acordo.cliente_nome_snapshot || '';
  return {
    versao: 1,
    devedor,
    coobrigado: null,
    anexoB: acordo.tipo_contrato === 'divida_saldo'
      ? anexoBPadrao({ dataAssinaturaTermo: cadastro.negociacao?.data_assinatura_termo })
      : null,
    faturas: (faturas || []).map(faturaParaTermo),
  };
}

/** Acordo de teste: todas as faturas dele (as de origem e a da entrada) são de
 *  teste (`ambiente='sandbox'`). O termo então sai no sandbox do Autentique —
 *  sem gastar crédito e sem mandar contrato de verdade para ninguém. */
async function acordoDeTeste(acordo: any): Promise<boolean> {
  const ids = [
    ...((acordo.faturas_origem_snapshot as any[]) || []).map((f) => f.id),
    acordo.entrada_fatura_id,
  ].filter((v) => v !== null && v !== undefined);
  if (ids.length === 0) return false;
  const { data } = await supabase.from('faturas').select('ambiente').in('id', ids);
  return !!data && data.length > 0 && data.every((f: any) => f.ambiente === 'sandbox');
}

export interface ContatosTermo {
  devedor: { email: string; whatsapp?: string | null };
  coobrigado?: { email: string; whatsapp?: string | null } | null;
}

/**
 * Envia o termo de um acordo JÁ criado — logo depois de criar, ou depois pela
 * tela de Parcelamentos. Monta o pedido a partir do que está gravado no acordo,
 * não do formulário: o termo tem que dizer exatamente o que o acordo diz. Só os
 * contatos podem mudar na hora de enviar.
 *
 * Os e-mails de quem assina são gravados ANTES de pedir o termo. É por eles que
 * a varredura reconhece as assinaturas do titular e do responsável; se a
 * gravação viesse depois e falhasse, o termo assinado não efetivaria nada.
 */
export async function enviarTermoAcordo(
  acordoId: string,
  contatos: ContatosTermo,
  usuario: { id?: string | null; nome?: string | null },
): Promise<RespostaTermo> {
  const { data: acordo, error } = await supabase
    .from('parcelamentos')
    .select('id, status, tipo_contrato, cliente_nome_snapshot, uc_codigo_snapshot, unidade_consumidora_id, created_at, memoria_calculo, multa_percent, mora_mensal_percent, saldo_kwh, saldo_tarifa, saldo_desconto_percent, faturas_origem_snapshot, entrada_fatura_id, termo_id_autentique, termo_dados')
    .eq('id', acordoId)
    .single();
  if (error) throw error;
  if (acordo.status !== 'aguardando_assinatura') {
    throw new Error('Só dá para enviar o termo de uma proposta aguardando assinatura.');
  }
  if (acordo.termo_id_autentique) {
    throw new Error('Este acordo já tem termo enviado. Para trocar o termo, cancele o acordo e crie outro.');
  }

  const base: DadosTermo = acordo.termo_dados ?? await montarDadosTermoLegado(acordo);
  const dados: DadosTermo = {
    ...base,
    devedor: {
      ...base.devedor,
      email: contatos.devedor.email.trim(),
      whatsapp: (contatos.devedor.whatsapp ?? base.devedor.whatsapp ?? '').trim(),
    },
    coobrigado: base.coobrigado
      ? {
        ...base.coobrigado,
        email: (contatos.coobrigado?.email ?? base.coobrigado.email).trim(),
        whatsapp: (contatos.coobrigado?.whatsapp ?? base.coobrigado.whatsapp ?? '').trim(),
      }
      : null,
  };

  const erros = validarDadosTermo(dados, { exigirContato: true, temSaldo: acordo.tipo_contrato === 'divida_saldo' });
  if (erros.length > 0) {
    throw new Error(
      acordo.termo_dados
        ? erros.join(' ')
        : `${erros.join(' ')} Este acordo é anterior ao termo novo; se faltar dado que a tela não pede, cancele e crie o acordo de novo.`,
    );
  }

  const { error: erroEmail } = await supabase
    .from('parcelamentos')
    .update({
      termo_signatario_cliente_email: dados.devedor.email,
      termo_signatario_coobrigado_email: dados.coobrigado?.email || null,
      termo_dados: dados,
    })
    .eq('id', acordoId);
  if (erroEmail) throw erroEmail;

  const [signatarios, sandbox] = await Promise.all([fetchSignatariosDefault(), acordoDeTeste(acordo)]);
  const payload = montarPayloadTermo({ acordo, dados, signatarios, sandbox });

  // Falha no termo fica gravada no acordo: o toast some em segundos, e o card
  // precisa continuar dizendo que o termo NÃO saiu (e por quê) para quem abrir depois.
  let resposta: RespostaTermo;
  try {
    resposta = await solicitarTermo(acordoId, payload, usuario);
  } catch (err: any) {
    const motivo = String(err?.message || err);
    await supabase.from('parcelamentos').update({ termo_envio_erro: motivo }).eq('id', acordoId);
    await registrarEvento(acordoId, 'falha', `Termo NÃO foi enviado: ${motivo}`, undefined, usuario);
    throw err;
  }

  // O boleto da entrada é emitido junto com o termo (decisão do Vinícius em
  // 24/09/2026), mas só vai para o cliente depois da assinatura (16/10/2026): quem
  // manda é a varredura. Nunca lança — o que falhar fica no card.
  const entrada = await emitirEEnviarEntrada(acordoId, usuario);
  return { ...resposta, entrada };
}

/** Efetivação manual — o analista tem o comprovante na mão. Passa pela MESMA
 *  edge function do cron: um só lugar move fatura-mãe. */
export async function efetivarAcordoManual(acordoId: string, motivo: string) {
  if (!motivo || motivo.trim().length < 10) {
    throw new Error('Descreva em pelo menos 10 caracteres por que está efetivando manualmente (fica na trilha).');
  }
  return invocar('efetivar-parcelamentos', {
    parcelamento_id: acordoId,
    manual: true,
    motivo: motivo.trim(),
  });
}

export async function cancelarAcordo(acordoId: string, motivo: string) {
  if (!motivo || motivo.trim().length < 10) {
    throw new Error('Descreva em pelo menos 10 caracteres o motivo do cancelamento (fica na trilha).');
  }
  return invocar('efetivar-parcelamentos', {
    parcelamento_id: acordoId,
    acao: 'cancelar',
    motivo: motivo.trim(),
  });
}

export async function aprovarAcordo(acordoId: string, usuario: { id?: string | null; nome?: string | null }) {
  const { data: atual, error: erroAtual } = await supabase
    .from('parcelamentos')
    .select('entrada_vencimento')
    .eq('id', acordoId)
    .single();
  if (erroAtual) throw erroAtual;

  // A validade conta da aprovação: enquanto esperava a alçada o cliente nem
  // podia receber o termo, e a varredura expiraria a proposta logo em seguida.
  const expiraEm = await calcularExpiracao(atual.entrada_vencimento);

  const { data, error } = await supabase
    .from('parcelamentos')
    .update({
      aprovado_por: usuario.id || null,
      aprovado_em: new Date().toISOString(),
      aprovacao_necessaria: false,
      status: 'aguardando_assinatura',
      expira_em: expiraEm,
    })
    .eq('id', acordoId)
    .eq('status', 'aguardando_aprovacao')
    .select('id');
  if (error) throw error;
  if (!data || data.length === 0) throw new Error('Esta proposta não está mais aguardando aprovação.');
  await registrarEvento(acordoId, 'aprovado', `Acordo aprovado pela alçada. Nova validade: ${new Date(expiraEm).toLocaleDateString('pt-BR')}.`, undefined, usuario);
}
